import { z } from "zod";

// TES-213 — the patient-facing Treatment surface: a dedicated view of the
// patient's medication journey built from their PrescriptionCycle history.
// Read-only; no PII beyond the dose label the doctor authored.
//
// TES-480 (Ö-26) — deliberately NARROW. The patient sees
// which prescription applies now / applied before / was replaced, the dose
// exactly as the doctor wrote it, when it was issued, and Ella's own cycle
// status. Nothing derived from the free-text dose, no validity/expiry, no
// renewal or next-dose arithmetic — those live in Webdoc and in the doctor's
// published follow-up, never here.

// How a cycle relates to the patient's present:
//   current    — the newest cycle still in a live state ("Gäller nu")
//   superseded — a cycle that has a successor in the chain ("Ersatt")
//   previous   — every other closed cycle ("Tidigare")
export const treatmentCycleLabelSchema = z.enum([
  "current",
  "previous",
  "superseded",
]);
export type TreatmentCycleLabel = z.infer<typeof treatmentCycleLabelSchema>;

export const treatmentCycleSchema = z.object({
  cycleNumber: z.number().int(),
  // Free-text medication + dosage label the doctor authored, e.g. "Wegovy 0,5
  // mg". Rendered verbatim (Ö-64) — never parsed.
  dose: z.string(),
  state: z.string(),
  prescribedAt: z.string().nullable(),
  label: treatmentCycleLabelSchema,
});
export type TreatmentCycle = z.infer<typeof treatmentCycleSchema>;

export const treatmentSurfaceSchema = z.object({
  // The cycle the patient is currently living through (active/expiring), or null
  // before treatment activation.
  current: treatmentCycleSchema.nullable(),
  // Every cycle, oldest → newest, for the history list.
  history: z.array(treatmentCycleSchema),
  // The latest published monthly follow-up, so the view can link to it
  // (GET patient-api/v1/medical-review/monthly/latest); null until the doctor
  // has published one.
  followUp: z
    .object({
      publishedAt: z.string(),
    })
    .nullable(),
  // Ö-137 —
  // "DT-01 och behandlingsdag 1 startar först när patienten registrerar sin faktiska första dos genom `first_dose_reported`."
  // The day counter hangs off HER report, so the app needs somewhere to make
  // it, and this surface is where her prescription already lives.
  //
  // `canReportFirstDose` is decided on the server, not by the client reading
  // `firstDoseReportedAt === null`: the rule has two other terms (a live cycle
  // to have a first dose OF, and a NEW_TREATMENT patient — a
  // prescription-transfer patient started her medication elsewhere, so no
  // first dose of ours describes it) and a rule split across two apps drifts.
  //
  // Both optional: a TestFlight build newer than the box would otherwise fail
  // the schema parse against an API that has not been deployed yet, and this
  // screen is not worth crashing over a field it can do without.
  firstDoseReportedAt: z.string().nullable().optional(),
  canReportFirstDose: z.boolean().optional(),
  // TES-1436 (Emelie 2026-10-19) — a prescription transfer's "Registrera din
  // första dos hos Ella", the transfer variant of CP-12: the first dose she
  // takes under Ella's care. It anchors her dose days and check-ins with
  // Ella and never her treatment day, which stays her verified historical
  // start (F-07). Offered in ACTIVE_TREATMENT and, inside CP-61, in
  // WEIGHT_STABILISATION. A separate field so `canReportFirstDose` keeps the
  // meaning released builds read (they word the screen for a new treatment).
  canReportFirstDoseInElla: z.boolean().optional(),
  // TES-903 — CP-12 variant A/B, from the current cycle's explicit
  // `medicationPickupRequired`, set only by the doctor's new-prescription
  // choice (TES-997): true = variant A (she has a medicine to collect),
  // false = variant B (existing prescription), null = unknown (older rows,
  // or no current cycle) — never guessed from the patient group.
  // `medicationPickupReportedAt` is her own pickup check-off for that cycle.
  // Optional for the same reason as the first-dose fields.
  medicationPickupRequired: z.boolean().nullable().optional(),
  medicationPickupReportedAt: z.string().nullable().optional(),
  // @deprecated — always []. The app build in TestFlight before TES-480 reads
  // `doseProgression.length`, and an absent field would crash its Treatment
  // screen against a newer API. Empty carries nothing Ö-26 hides. Remove once
  // the next build has shipped.
  doseProgression: z.array(z.string()).max(0),
});
export type TreatmentSurface = z.infer<typeof treatmentSurfaceSchema>;

// Ö-140 — what the patient confirms when she registers her
// first dose: the DATE she took it, not the moment she tells us.
//
// A plain calendar date (YYYY-MM-DD), deliberately. A dose is something that
// happened on a day, in her timezone, and asking a phone for an instant would
// invite an off-by-one the moment she reports late in the evening. The server
// anchors the day counter on it.
export const reportFirstDoseSchema = z.object({
  doseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "doseDate must be YYYY-MM-DD"),
});
export type ReportFirstDoseRequest = z.infer<typeof reportFirstDoseSchema>;

// TES-1160 / TES-1165 — her answer to a planned dose, from "Registrera dos":
//   taken     — "Dosen är tagen", with `takenOn`: the day she actually took
//               it ("När tog du dosen?"), the planned day preselected
//   not_taken — "Jag tog ingen dos"
// "Jag tar den senare" is not an answer: it sends nothing and the task stays
// open. `plannedOn` names the planned dose the answer belongs to (Ö-385 b):
// the one dose that is open for her right now (GET
// patient-api/v1/treatment/dose), whose answer closes that task and is
// logged as the DomainEvent `dose_reported`, or (TES-1151, Ö-416) any
// earlier planned dose she never answered, which enters her dose log only.
// One standing answer per planned dose.
export const DOSE_OUTCOMES = ["taken", "not_taken"] as const;
export type DoseOutcome = (typeof DOSE_OUTCOMES)[number];
const ISO_CALENDAR_DATE = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD");

// TES-1151 S6 (Ö-415, Emelie 2026-10-15): "Orsak till avvikande eller
// utebliven dos är frivillig och väljs primärt ur en strukturerad lista.
// Biverkningar/besvär öppnar det gemensamma besvärsflödet. Annat kan ha en
// kort frivillig fritext." Optional, and only on an answer that deviates
// from the plan: "Jag tog ingen dos", or a dose taken on another day than
// planned. `reasonNote` only with `other`, at most DOSE_REASON_NOTE_MAX
// characters. TES-1438 (Emelie 2026-10-19): `side_effects` is context about
// the dose and opens no symptom flow — "Dosregistreringen ska inte ha egen
// besvärsregistrering eller länk/påminnelse till sådan.", which replaces
// Ö-415's "Biverkningar/besvär öppnar det gemensamma besvärsflödet".
export const DOSE_REASONS = [
  "side_effects",
  "forgot",
  "no_medication",
  "other",
] as const;
export type DoseReason = (typeof DOSE_REASONS)[number];
// The labels for side_effects and other are Ö-415's own words. The labels
// for forgot and no_medication come from the list the spec proposed for
// question D3; Ö-415 confirmed that list without naming every item.
export const DOSE_REASON_LABELS: Record<DoseReason, string> = {
  side_effects: "Biverkningar/besvär",
  forgot: "Glömde",
  no_medication: "Hade inget läkemedel",
  other: "Annat",
};
// "kort frivillig fritext": our assumption of what short means.
export const DOSE_REASON_NOTE_MAX = 200;
const doseReasonFields = {
  reason: z.enum(DOSE_REASONS).optional(),
  reasonNote: z.string().trim().min(1).max(DOSE_REASON_NOTE_MAX).optional(),
};

export const reportDoseSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("taken"),
    plannedOn: ISO_CALENDAR_DATE,
    takenOn: ISO_CALENDAR_DATE,
    ...doseReasonFields,
  }),
  z.object({
    outcome: z.literal("not_taken"),
    plannedOn: ISO_CALENDAR_DATE,
    ...doseReasonFields,
  }),
]);
export type ReportDoseRequest = z.infer<typeof reportDoseSchema>;

// TES-1165 — the planned dose she can register right now, or null. A planned
// dose stays open from its day until the next planned dose day; a late
// registration never moves the schedule. `takenOn` may be any day from
// `earliestTakenOn` (the day after the previous planned dose) to
// `latestTakenOn` (today, Stockholm): no future date.
export const openDoseSchema = z.object({
  plannedOn: ISO_CALENDAR_DATE,
  earliestTakenOn: ISO_CALENDAR_DATE,
  latestTakenOn: ISO_CALENDAR_DATE,
});
export type OpenDose = z.infer<typeof openDoseSchema>;
export const openDoseResponseSchema = z.object({
  openDose: openDoseSchema.nullable(),
});
export type OpenDoseResponse = z.infer<typeof openDoseResponseSchema>;

// TES-1151 S8 (Ö-417) — she may correct her own answer to a planned dose
// for this long after she first answered it (POST
// patient-api/v1/treatment/dose/correction, the same body as POST /dose).
// After that the correction goes through Kontakta Ella.
export const DOSE_CORRECTION_WINDOW_HOURS = 24;

// GET patient-api/v1/treatment/dose/registration?plannedOn=YYYY-MM-DD — her
// standing answer to one planned dose, or null. `id` is the dose log row a
// symptom registered from the dose names (POST patient-api/v1/symptoms,
// `doseEventId`). `firstReportedAt` is her first answer to this planned
// dose; a correction keeps it. `correctable` is false from
// `correctableUntil` on, when the app offers the contact route instead.
export const doseRegistrationSchema = z.object({
  id: z.string(),
  plannedOn: ISO_CALENDAR_DATE,
  outcome: z.enum(DOSE_OUTCOMES),
  takenOn: ISO_CALENDAR_DATE.nullable(),
  // TES-1151 S6 (Ö-415) — her optional reason, and her "Annat" text.
  reason: z.enum(DOSE_REASONS).nullable(),
  reasonNote: z.string().nullable(),
  firstReportedAt: z.string(),
  correctableUntil: z.string(),
  correctable: z.boolean(),
});
export type DoseRegistration = z.infer<typeof doseRegistrationSchema>;
export const doseRegistrationResponseSchema = z.object({
  registration: doseRegistrationSchema.nullable(),
});
export type DoseRegistrationResponse = z.infer<
  typeof doseRegistrationResponseSchema
>;

// TES-1527 (Ö-776, Ö-766, Emelie 2026-10-21) — "Registrera tagen dos", the
// entry she can use every day: "Patienten ska varje dag kunna registrera en
// faktiskt tagen dos idag eller bakåt inom beslutad behandlingsstartgräns,
// oavsett planerad dosdag och även om veckans rad redan är klar." She names
// only the day she took it; the server decides which planned dose it answers
// (apps/api cycle/dose-day.ts reportTakenDose), so the app computes no
// planned day.
//
// GET patient-api/v1/treatment/dose/taken — the days the picker offers:
// `earliest` is the day after her first dose in Ella, and never before her
// treatment start (TES-1489, Ö-669); `latest` is today (Stockholm), never a
// future day. Null when no dose can be registered this way (no first dose,
// no weekly ordination, an ended treatment). A day inside the range may
// still be refused, e.g. a week a doctor's pause closed or one she already
// answered.
export const takenDoseWindowResponseSchema = z.object({
  window: z
    .object({ earliest: ISO_CALENDAR_DATE, latest: ISO_CALENDAR_DATE })
    .nullable(),
});
export type TakenDoseWindowResponse = z.infer<
  typeof takenDoseWindowResponseSchema
>;

// POST patient-api/v1/treatment/dose/taken — "Dosen är tagen" on `takenOn`.
// The answer is `{ plannedOn }`: the planned dose it was registered against,
// which may lie after today when she took the dose before its planned day.
export const reportTakenDoseSchema = z.object({ takenOn: ISO_CALENDAR_DATE });
export type ReportTakenDoseRequest = z.infer<typeof reportTakenDoseSchema>;
export const reportTakenDoseResponseSchema = z.object({
  plannedOn: ISO_CALENDAR_DATE,
});
export type ReportTakenDoseResponse = z.infer<
  typeof reportTakenDoseResponseSchema
>;

// TES-903 — the CTA path of Hem's derived "Hämta ut ditt läkemedel" card.
// Not a route: the app performs the check-off in place
// (POST patient-api/v1/treatment/medication-pickup) and refreshes Hem.
// Deliberately absent from DEEP_LINK_STATIC_PATHS — it is not something an
// author may link to, and an OS-level open of it has nothing to show.
export const MEDICATION_PICKUP_REPORT_CTA =
  "carebyella://medication-pickup/report";
